import {
  JEWEL_DEFINITIONS,
  JEWEL_LEVEL_MAX,
  type JewelSocketProfile,
  toExactBigInt,
} from '@kenocyber/shared';

/**
 * O teto, uma vez e em `bigint`.
 *
 * Derivado de {@link JEWEL_LEVEL_MAX}, que por sua vez é lido do catálogo - o
 * mesmo 30 que a base impõe. Escrever `30n` aqui seria um terceiro sítio a
 * concordar por coincidência.
 */
const JEWEL_LEVEL_CAP = BigInt(JEWEL_LEVEL_MAX);

/**
 * **O único sítio que sabe o que uma joia FAZ.**
 *
 * ## O problema que este ficheiro resolve
 *
 * Há dois sítios que aplicam dano - a caça e o duelo - e vão ser mais. Um
 * `if (entry.id === 'jewel_damage')` em cada um seria a mesma decisão escrita
 * duas vezes, e a segunda a divergir seria aquela em que ninguém tocou. Pior:
 * cada efeito novo multiplicaria os sítios, e a lei aditiva do contrato V28D.2 -
 * que é o que impede um coração de multiplicar uma joia e uma joia de
 * multiplicar um coração - passaria a depender de cada call site se lembrar
 * dela.
 *
 * Então há um lugar que traduz um PERFIL em NÚMEROS, e quem aplica dano soma um
 * `bigint` sem nunca ter ouvido falar de `jewel_damage`.
 *
 * ## Puro, e o que isso quer dizer exatamente
 *
 * Sem I/O, sem relógio, sem aleatoriedade, sem estado. A mesma entrada dá
 * sempre a mesma saída, e a saída não partilha objeto nenhum com a entrada.
 * O catálogo é lido UMA VEZ, ao carregar o módulo, para um mapa congelado -
 * portanto durante a simulação não há consulta nenhuma, só uma leitura de
 * `Map`. É a diferença entre "o resolvedor conhece o catálogo" e "a simulação
 * consulta o catálogo", e só a segunda seria um problema.
 *
 * ## Porque `bigint`
 *
 * Porque o resultado é somado à mesma parcela onde entram os Corações
 * Primordiais, cuja contagem não tem tecto de jogo. Um `number` ali seria um
 * tecto de item escrito num tipo, que é o que o `CLAUDE.md` proíbe.
 *
 * ## O que este ficheiro NÃO faz
 *
 * Não aplica dano, não conhece um alvo, não sabe o que é um projétil e não sabe
 * o que é uma runa. Devolve o que o perfil VALE; quem decide o que fazer com
 * isso é `damage/exact.ts`, e é lá que a ordem canónica vive.
 */

/**
 * O que um perfil de joias vale, em unidades inteiras.
 *
 * Uma interface com um campo hoje, e é deliberado que seja uma interface: as
 * catorze mecânicas do contrato produzem grandezas diferentes - vida máxima,
 * cadência, redução, chance de proc - e a forma em que a primeira chega decide
 * a forma em que as outras vão chegar. Um `bigint` solto obrigaria o gate
 * seguinte a mudar todas as assinaturas.
 */
export interface JewelDerivedEffects {
  /**
   * Percentagem SOMADA ao tiro normal, em basis points.
   *
   * Uma parcela, nunca um fator. Entra no mesmo `summedBps` de
   * `shotDamageMilli` onde já entram a nave, os artefactos, o equipamento e os
   * Corações - por isso `+900` ao lado de `+2 500` é `+3 400`, e nunca
   * `1,09 x 1,25`.
   */
  readonly damageBps: bigint;
  /**
   * Percentagem de CADÊNCIA somada, em basis points (gate V28G.D).
   *
   * Uma parcela sobre a taxa-base autoritativa da nave, e nunca um fator sobre
   * o intervalo. Quem a consome converte-a em intervalo pela fórmula aprovada -
   * `base x 10000 / (10000 + bps)` no PvP, `taxa x (1 + bps/10000)` na caça - e
   * nenhum dos dois a multiplica por outra percentagem.
   *
   * Não tem relação nenhuma com {@link JewelDerivedEffects.damageBps}: as duas
   * chegam do mesmo perfil e vão para sítios diferentes. Somá-las seria aplicar
   * cadência como dano.
   */
  readonly attackSpeedBps: bigint;
  /**
   * Percentagem do DANO APLICADO devolvida em vida, em basis points
   * (gate V28G.I).
   *
   * ## Um coeficiente estatístico, e nada mais
   *
   * Não é uma chance, não é um proc e não passa pela loteria: é o número por que
   * um golpe confirmado é multiplicado para dar a cura. Quem o aplica não sabe
   * que joia o produziu, não sabe o que é `levelSum` e não pergunta ao catálogo
   * — recebe um `bigint` e multiplica, que é exatamente o que faz as duas
   * simulações não terem um `if (jewel_lifesteal)` em lado nenhum.
   *
   * ## Já vem com o TETO aplicado
   *
   * Por joia, antes da soma. `levelSum` já é `L x n` agregado por definição,
   * portanto cortar por entrada é cortar por joia — e é o que permite a uma
   * segunda joia de cura contínua ter o seu próprio teto sem que o corte de uma
   * coma o da outra.
   *
   * Não tem relação nenhuma com {@link JewelDerivedEffects.damageBps}: aquele
   * soma-se ao tiro, este multiplica o que o tiro removeu. Somá-los seria pagar
   * cura como dano.
   */
  readonly lifestealBps: bigint;
  /**
   * Percentagem de todo o dano hostil RECEBIDO que deixa de acontecer, em basis
   * points (gate V28G.Q).
   *
   * ## O primeiro coeficiente deste ficheiro que descreve o golpe de outra
   * pessoa
   *
   * Os três acima são do lado de quem ataca - quanto o tiro vale, com que
   * frequência sai, e que fração do que ele removeu volta em vida. Este é do
   * lado de quem DEFENDE, e o que ele multiplica é um payload que outro casco
   * formou.
   *
   * É por isso que ele não é somado a nada: quem o consome converte-o num
   * MULTIPLICADOR - `10 000 - este` - e aplica-o uma vez. Somá-lo a
   * {@link JewelDerivedEffects.damageBps} por serem os dois percentagens de dano
   * faria uma nave reduzir o dano dela própria.
   *
   * ## Já vem com o TETO aplicado
   *
   * Por joia, antes da soma, como o do Orbe Vampírico e pela mesma razão: uma
   * segunda joia de redução teria o seu, e somar primeiro para cortar depois
   * faria o excedente de uma comer o espaço da outra.
   */
  readonly damageReductionBps: bigint;
  /**
   * Percentagem de VIDA MÁXIMA somada, em basis points (gate V28G.R).
   *
   * ## O primeiro coeficiente deste ficheiro que não descreve um golpe
   *
   * Os três primeiros são do lado de quem ataca e o quarto é do lado de quem
   * recebe - mas os quatro falam de dano. Este não fala: ele descreve o TETO de
   * um casco, e entra numa composição que acontece uma vez por sessão, quando
   * o casco é formado, e não uma vez por golpe.
   *
   * ## Uma parcela, nunca um fator
   *
   * Entra no mesmo `summedBps` de {@link composeMaxHealth} onde já entram as
   * percentagens de vida dos artefactos e do equipamento - por isso `+6 000` ao
   * lado de `+2 000` é `+8 000`, e nunca `1,60 x 1,20`. É a mesma lei aditiva
   * que `damageBps` obedece do lado do tiro.
   *
   * ## Já vem com o TETO aplicado
   *
   * Por joia, antes da soma, como o da cura e o da redução, e pela mesma razão:
   * uma segunda joia de vida teria o seu, e somar primeiro para cortar depois
   * faria o excedente de uma comer o espaço da outra.
   */
  readonly maxHealthBps: bigint;
  /**
   * Percentagem de VELOCIDADE DE MOVIMENTO somada, em basis points
   * (gate V28G.T).
   *
   * ## A primeira grandeza deste ficheiro que não fala de dano nem de vida
   *
   * As quatro primeiras descrevem um golpe - quanto ele vale, com que frequência
   * sai, que fração dele volta em vida, e que fração de um golpe alheio deixa de
   * acontecer. A quinta descreve o TETO de um casco. Esta descreve o
   * DESLOCAMENTO VOLUNTÁRIO dele: quanto mais depressa ele anda quando o jogador
   * o manda andar, ou quando a caça o faz vaguear.
   *
   * ## Uma parcela, nunca um fator
   *
   * Entra no mesmo agrupamento percentual onde já entra o bónus de velocidade do
   * próprio casco - por isso `+2 000` ao lado de `+3 900` é `+5 900`, e nunca
   * `1,20 x 1,39`. É a mesma lei aditiva que `damageBps` obedece do lado do tiro
   * e que `maxHealthBps` obedece do lado do casco.
   *
   * ## O que ela NÃO multiplica
   *
   * Nada que a nave não tenha pedido. Um empurrão de bomba, um teleporte, uma
   * correção da autoridade, um reposicionamento de spawn e um knockback são
   * deslocamentos que as REGRAS impõem, e nenhum deles passa pelo passo que esta
   * percentagem escala. Quem garante isso não é este comentário - é o facto de a
   * percentagem ser gasta num único sítio por modo, e esse sítio ser o passo do
   * jogador.
   *
   * ## Já vem com o TETO aplicado
   *
   * Por joia, antes da soma, como o da cura, o da redução e o da vida. Aqui o
   * teto é a própria promessa do contrato - **+39 % é absoluto** - e é por isso
   * que ele é aplicado duas vezes por caminhos independentes: o `min` do nível e
   * o `min` do coeficiente.
   */
  readonly moveSpeedBps: bigint;
}

/**
 * Nenhum efeito, partilhado e congelado.
 *
 * O caso comum em todas as partidas e caçadas de hoje, e devolvido por
 * IDENTIDADE: um chamador pode comparar com `===` para saltar trabalho, e não
 * há alocação por cadeira para descrever a mesma ausência seis vezes.
 */
export const NO_JEWEL_EFFECTS: JewelDerivedEffects = Object.freeze({
  damageBps: 0n,
  attackSpeedBps: 0n,
  lifestealBps: 0n,
  damageReductionBps: 0n,
  maxHealthBps: 0n,
  moveSpeedBps: 0n,
});

/**
 * O coeficiente de cada joia, lido do catálogo uma vez.
 *
 * Um `Map` construído ao carregar o módulo, e não uma procura por definição a
 * cada tiro. As joias sem coeficiente simplesmente não estão aqui, o que faz
 * "esta joia não concede nada" ser uma ausência em vez de um zero que alguém
 * teria de escrever catorze vezes.
 *
 * Derivado do catálogo e nunca de uma segunda lista: uma lista manual
 * concordaria consigo mesma para sempre, incluindo no dia em que o catálogo
 * mudasse - que é precisamente o dia em que um efeito novo é ligado.
 */
const DAMAGE_PER_LEVEL_BPS: ReadonlyMap<string, bigint> = new Map(
  JEWEL_DEFINITIONS.filter(
    (jewel) => jewel.socketMode === 'socketed' && jewel.effects.damagePerLevelSumBps !== undefined,
  ).map((jewel) => [jewel.definitionId, BigInt(jewel.effects.damagePerLevelSumBps ?? 0)]),
);

/**
 * O coeficiente de cadência de cada joia, pela mesma leitura única.
 *
 * Um segundo mapa e não um campo a mais no primeiro: as duas grandezas são
 * independentes, uma joia pode conceder uma sem a outra, e uma entrada ausente
 * continua a ser "esta joia não concede cadência" em vez de um zero que alguém
 * teria de escrever. `jewel_attack_speed` não é nomeada aqui - ela entra por ter
 * o campo no catálogo, que é o que mantém este ficheiro como o ÚNICO que sabe
 * o que uma joia faz sem saber qual delas é.
 */
const ATTACK_SPEED_PER_LEVEL_BPS: ReadonlyMap<string, bigint> = new Map(
  JEWEL_DEFINITIONS.filter(
    (jewel) =>
      jewel.socketMode === 'socketed' && jewel.effects.attackSpeedPerLevelSumBps !== undefined,
  ).map((jewel) => [jewel.definitionId, BigInt(jewel.effects.attackSpeedPerLevelSumBps ?? 0)]),
);

/**
 * O coeficiente de CURA de cada joia, e o seu teto, pela mesma leitura única.
 *
 * Um par por joia e não dois mapas: o coeficiente e o teto são a mesma decisão
 * de equilíbrio partida em dois números, e separá-los deixaria uma joia com
 * coeficiente e sem teto - que é uma joia sem limite escrita por descuido.
 *
 * `jewel_lifesteal` não é nomeada aqui. Ela entra por ter os campos no catálogo,
 * que é o que mantém este ficheiro como o único que sabe o que uma joia faz sem
 * saber qual delas é - e o que faz o Coração Vital ligar-se um dia acrescentando
 * uma entrada ao catálogo em vez de um ramo a este ciclo.
 */
interface LifestealCoefficient {
  readonly perLevelBps: bigint;
  readonly maxBps: bigint;
}

const LIFESTEAL_PER_LEVEL_BPS: ReadonlyMap<string, LifestealCoefficient> = new Map(
  JEWEL_DEFINITIONS.filter(
    (jewel) =>
      jewel.socketMode === 'socketed' && jewel.effects.lifestealPerLevelSumBps !== undefined,
  ).map((jewel) => {
    if (jewel.effects.lifestealMaxBps === undefined) {
      /*
       * Ao CARREGAR o módulo, e não no primeiro golpe que a pedisse.
       *
       * Uma joia de cura sem teto declarado é uma decisão de equilíbrio por
       * fazer, e o momento de a recusar é aquele em que ela ainda não pode ter
       * curado ninguém.
       */
      throw new RangeError(`${jewel.definitionId} grants lifesteal with no declared ceiling`);
    }
    return [
      jewel.definitionId,
      Object.freeze({
        perLevelBps: BigInt(jewel.effects.lifestealPerLevelSumBps ?? 0),
        maxBps: BigInt(jewel.effects.lifestealMaxBps),
      }),
    ];
  }),
);

/**
 * O coeficiente de REDUÇÃO de cada joia, e o seu teto, pela mesma leitura única.
 *
 * Um par por joia e não dois mapas, pela mesma razão que o da cura: o
 * coeficiente e o teto são a mesma decisão de equilíbrio partida em dois
 * números, e separá-los deixaria uma joia com coeficiente e sem teto - que é uma
 * joia sem limite escrita por descuido.
 *
 * Nenhuma joia é nomeada aqui. Ela entra por ter os campos no catálogo, que é o
 * que mantém este ficheiro como o único que sabe o que uma joia faz sem saber
 * qual delas é.
 */
interface DamageReductionCoefficient {
  readonly perLevelBps: bigint;
  readonly maxBps: bigint;
}

const DAMAGE_REDUCTION_PER_LEVEL_BPS: ReadonlyMap<string, DamageReductionCoefficient> = new Map(
  JEWEL_DEFINITIONS.filter(
    (jewel) =>
      jewel.socketMode === 'socketed' && jewel.effects.damageReductionPerLevelSumBps !== undefined,
  ).map((jewel) => {
    if (jewel.effects.damageReductionMaxBps === undefined) {
      /*
       * Ao CARREGAR o módulo, e não no primeiro golpe que a pedisse.
       *
       * Uma joia de redução sem teto declarado é uma decisão de equilíbrio por
       * fazer - e, pior do que na cura, uma sem teto podia levar o multiplicador
       * a zero e tornar uma nave imune. O momento de a recusar é aquele em que
       * ela ainda não pode ter poupado ninguém.
       */
      throw new RangeError(
        `${jewel.definitionId} grants damage reduction with no declared ceiling`,
      );
    }
    return [
      jewel.definitionId,
      Object.freeze({
        perLevelBps: BigInt(jewel.effects.damageReductionPerLevelSumBps ?? 0),
        maxBps: BigInt(jewel.effects.damageReductionMaxBps),
      }),
    ];
  }),
);

/**
 * O coeficiente de VIDA MÁXIMA de cada joia, e o seu teto, pela mesma leitura
 * única.
 *
 * Um par por joia e não dois mapas, pela mesma razão que o da cura e o da
 * redução: o coeficiente e o teto são a mesma decisão de equilíbrio partida em
 * dois números, e separá-los deixaria uma joia com coeficiente e sem teto - que
 * é uma joia sem limite escrita por descuido.
 *
 * Nenhuma joia é nomeada aqui. Ela entra por ter os campos no catálogo, que é o
 * que mantém este ficheiro como o único que sabe o que uma joia faz sem saber
 * qual delas é.
 */
interface MaxHealthCoefficient {
  readonly perLevelBps: bigint;
  readonly maxBps: bigint;
}

const MAX_HEALTH_PER_LEVEL_BPS: ReadonlyMap<string, MaxHealthCoefficient> = new Map(
  JEWEL_DEFINITIONS.filter(
    (jewel) =>
      jewel.socketMode === 'socketed' && jewel.effects.maxHealthPerLevelSumBps !== undefined,
  ).map((jewel) => {
    if (jewel.effects.maxHealthMaxBps === undefined) {
      /*
       * Ao CARREGAR o módulo, e não na primeira sessão que a pedisse.
       *
       * Uma joia de vida sem teto declarado é uma decisão de equilíbrio por
       * fazer, e o momento de a recusar é aquele em que ela ainda não pode ter
       * formado casco nenhum.
       */
      throw new RangeError(`${jewel.definitionId} grants maximum health with no declared ceiling`);
    }
    return [
      jewel.definitionId,
      Object.freeze({
        perLevelBps: BigInt(jewel.effects.maxHealthPerLevelSumBps ?? 0),
        maxBps: BigInt(jewel.effects.maxHealthMaxBps),
      }),
    ];
  }),
);

/**
 * O coeficiente de VELOCIDADE de cada joia, e o seu teto, pela mesma leitura
 * única.
 *
 * Um par por joia e não dois mapas, pela mesma razão que os três anteriores: o
 * coeficiente e o teto são a mesma decisão de equilíbrio partida em dois
 * números, e separá-los deixaria uma joia com coeficiente e sem teto - que é uma
 * joia sem limite escrita por descuido.
 *
 * Nenhuma joia é nomeada aqui. Ela entra por ter os campos no catálogo, que é o
 * que mantém este ficheiro como o único que sabe o que uma joia faz sem saber
 * qual delas é.
 */
interface MoveSpeedCoefficient {
  readonly perLevelBps: bigint;
  readonly maxBps: bigint;
}

const MOVE_SPEED_PER_LEVEL_BPS: ReadonlyMap<string, MoveSpeedCoefficient> = new Map(
  JEWEL_DEFINITIONS.filter(
    (jewel) =>
      jewel.socketMode === 'socketed' && jewel.effects.moveSpeedPerLevelSumBps !== undefined,
  ).map((jewel) => {
    if (jewel.effects.moveSpeedMaxBps === undefined) {
      /*
       * Ao CARREGAR o módulo, e não no primeiro passo que a pedisse.
       *
       * Uma joia de movimento sem teto declarado é uma decisão de equilíbrio por
       * fazer, e o momento de a recusar é aquele em que ela ainda não pode ter
       * movido ninguém.
       */
      throw new RangeError(`${jewel.definitionId} grants movement speed with no declared ceiling`);
    }
    return [
      jewel.definitionId,
      Object.freeze({
        perLevelBps: BigInt(jewel.effects.moveSpeedPerLevelSumBps ?? 0),
        maxBps: BigInt(jewel.effects.moveSpeedMaxBps),
      }),
    ];
  }),
);

/**
 * O que este perfil vale, agora.
 *
 * ## A magnitude, e porque é `coef x levelSum`
 *
 * A lei 3 do contrato V28D.2 é `coef(L, n) = coef(1) x L x n` - cópias somam
 * MAGNITUDE, nunca entidades e nunca frequência. `levelSum` já é `L x n`
 * agregado por definição, portanto multiplicar outra vez pela contagem daria o
 * quadrado. `copies` é lido para NADA aqui, e essa ausência é a lei.
 *
 * ## A ordem não importa, e não pode importar
 *
 * A soma é comutativa e é a única operação. Um perfil escrito por outra ordem
 * produz o mesmo `bigint` - o que também torna esta função insensível a uma
 * futura mudança na ordem canónica.
 *
 * ## Recusa em vez de arredondar
 *
 * Um `levelSum` que não seja um inteiro seguro é um perfil que nunca devia ter
 * chegado aqui: o esquema partilhado recusa-o, o bilhete recusa-o e o snapshot
 * recusa-o. Encontrar um significa que uma dessas três falhou, e continuar em
 * silêncio seria construir dano a partir de um estado que não devia existir.
 */
export function resolveJewelEffects(profile: JewelSocketProfile): JewelDerivedEffects {
  let damageBps = 0n;
  let attackSpeedBps = 0n;
  let lifestealBps = 0n;
  let damageReductionBps = 0n;
  let maxHealthBps = 0n;
  let moveSpeedBps = 0n;
  for (const entry of profile.entries) {
    const damagePerLevel = DAMAGE_PER_LEVEL_BPS.get(entry.id);
    const speedPerLevel = ATTACK_SPEED_PER_LEVEL_BPS.get(entry.id);
    const lifestealPerLevel = LIFESTEAL_PER_LEVEL_BPS.get(entry.id);
    const reductionPerLevel = DAMAGE_REDUCTION_PER_LEVEL_BPS.get(entry.id);
    const maxHealthPerLevel = MAX_HEALTH_PER_LEVEL_BPS.get(entry.id);
    const moveSpeedPerLevel = MOVE_SPEED_PER_LEVEL_BPS.get(entry.id);
    if (
      damagePerLevel === undefined &&
      speedPerLevel === undefined &&
      lifestealPerLevel === undefined &&
      reductionPerLevel === undefined &&
      maxHealthPerLevel === undefined &&
      moveSpeedPerLevel === undefined
    ) {
      continue;
    }
    /*
     * `levelSum` é lido UMA vez por entrada, e as duas grandezas partilham-no.
     * Uma joia que concedesse ambas somaria a mesma soma de níveis às duas -
     * que é a lei 3 aplicada a cada grandeza, e não uma divisão do orçamento
     * entre elas.
     */
    const raw = toExactBigInt(entry.levelSum, `${entry.id} levelSum`);
    if (raw < 0n) {
      throw new RangeError(`${entry.id} has a negative levelSum: ${raw}`);
    }
    /*
     * **Nada cresce depois do 30** (gate V28G.K).
     *
     * `levelSum` é o nível de uma joia - uma skin tem uma de cada tipo - e o
     * nível para nos 30. O clamp é DEFENSIVO: um perfil com 31 já foi recusado
     * pelo schema, pelo socket e pela base antes de chegar aqui, e este `min`
     * existe para que a resposta a um que escape seja "o mesmo que 30" em vez de
     * "mais poder". Cortar não legaliza o dado - só o impede de pagar.
     */
    const levelSum = raw < JEWEL_LEVEL_CAP ? raw : JEWEL_LEVEL_CAP;
    damageBps += (damagePerLevel ?? 0n) * levelSum;
    attackSpeedBps += (speedPerLevel ?? 0n) * levelSum;
    if (lifestealPerLevel !== undefined) {
      /*
       * O TETO, por joia e antes da soma (gate V28G.I).
       *
       * `min(levelSum x coef, teto)`. Cortar aqui e não no total é o que faz uma
       * segunda joia de cura contínua ter o seu próprio limite: somar primeiro e
       * cortar depois faria o excedente de uma comer o espaço da outra, que é
       * uma interação de equilíbrio que ninguém decidiu.
       *
       * Exato e em `bigint`: o teto do nível não limita o PRODUTO - um
       * coeficiente futuro pode ser grande - portanto ele tem de caber antes de
       * ser comparado, e um `number` aqui seria um tecto escrito num tipo.
       */
      const granted = lifestealPerLevel.perLevelBps * levelSum;
      lifestealBps += granted < lifestealPerLevel.maxBps ? granted : lifestealPerLevel.maxBps;
    }
    if (reductionPerLevel !== undefined) {
      /*
       * O TETO, por joia e antes da soma (gate V28G.Q).
       *
       * `min(levelSum x coef, teto)`, exatamente como a cura. Aqui ele carrega
       * uma garantia a mais e ela é a que interessa: com `teto <= 10 000`, o
       * multiplicador que `hostileDamageMultiplierBps` produz nunca chega a
       * zero, portanto nenhum perfil - nem um corrompido que atravessasse todas
       * as fronteiras - pode tornar uma nave imune.
       */
      const granted = reductionPerLevel.perLevelBps * levelSum;
      damageReductionBps += granted < reductionPerLevel.maxBps ? granted : reductionPerLevel.maxBps;
    }
    if (maxHealthPerLevel !== undefined) {
      /*
       * O TETO, por joia e antes da soma (gate V28G.R).
       *
       * `min(levelSum x coef, teto)`, exatamente como a cura e a redução. Cortar
       * aqui e não no total é o que faz uma segunda joia de vida ter o seu
       * próprio limite: somar primeiro e cortar depois faria o excedente de uma
       * comer o espaço da outra.
       *
       * Não há teto SUPERIOR partilhado e não pode haver: o que sai daqui é
       * somado às percentagens de vida dos artefactos e do equipamento, que não
       * têm tecto nenhum, e escrever um aqui seria capar a vida de uma nave por
       * causa de uma joia.
       */
      const granted = maxHealthPerLevel.perLevelBps * levelSum;
      maxHealthBps += granted < maxHealthPerLevel.maxBps ? granted : maxHealthPerLevel.maxBps;
    }
    if (moveSpeedPerLevel !== undefined) {
      /*
       * O TETO, por joia e antes da soma (gate V28G.T).
       *
       * `min(levelSum x coef, teto)`, exatamente como a cura, a redução e a vida.
       * Aqui ele carrega a promessa do contrato e ela é a que interessa:
       * **+39 % é absoluto**. O `min` do nível acima e este `min` são dois cortes
       * independentes sobre a mesma grandeza, e é preciso que os dois falhem para
       * um perfil adulterado comprar um passo maior do que a tabela promete.
       */
      const granted = moveSpeedPerLevel.perLevelBps * levelSum;
      moveSpeedBps += granted < moveSpeedPerLevel.maxBps ? granted : moveSpeedPerLevel.maxBps;
    }
  }
  /*
   * A ausência continua a ser devolvida por IDENTIDADE, e exige que TODAS as
   * grandezas sejam nulas. Um perfil com uma joia de cadência já não é
   * "sem efeitos" só porque não concede dano, e um chamador que compare com
   * `===` para saltar trabalho continua a poder fazê-lo com segurança.
   */
  return damageBps === 0n &&
    attackSpeedBps === 0n &&
    lifestealBps === 0n &&
    damageReductionBps === 0n &&
    maxHealthBps === 0n &&
    moveSpeedBps === 0n
    ? NO_JEWEL_EFFECTS
    : Object.freeze({
        damageBps,
        attackSpeedBps,
        lifestealBps,
        damageReductionBps,
        maxHealthBps,
        moveSpeedBps,
      });
}
